import type { SavedView } from '../../../data/client';
import {
  Button,
  cx,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
  DropdownSeparator,
  Icon,
} from '../../../ui';

/**
 * WHICH VIEW THIS LIST IS (D14, D19), and everything a person can do to it.
 *
 * ONE MENU rather than a row of tabs. Tabs are the obvious shape and they are wrong
 * here: views are per object and unbounded, five to fifteen people share one set, and a
 * tab strip that scrolls sideways on a phone is the density decision this product keeps
 * refusing. The menu also has room for the verbs - save, rename, delete, make default -
 * which a tab strip has to hide behind a second control anyway.
 *
 * "UNSAVED CHANGES" IS A WORD, NOT A DOT. A coloured dot beside a view name is a
 * convention people learn; a sentence is one nobody has to.
 */
export interface ViewSwitcherProps {
  readonly views: readonly SavedView[];
  readonly current: SavedView | undefined;
  readonly dirty: boolean;
  readonly onSelect: (viewId: string | null) => void;
  readonly onSave: () => void;
  readonly onSaveAs: () => void;
  readonly onRename: () => void;
  readonly onSetDefault: () => void;
  readonly onDelete: () => void;
  readonly isSaving: boolean;
  /**
   * Layout from the row this sits in. The phone's single control row makes the
   * switcher the flexible half beside a fixed Adjust button, so the view NAME is
   * what gives way - which is why the label truncates rather than wraps.
   */
  readonly className?: string | undefined;
}

export function ViewSwitcher({
  views,
  current,
  dirty,
  onSelect,
  onSave,
  onSaveAs,
  onRename,
  onSetDefault,
  onDelete,
  isSaving,
  className,
}: ViewSwitcherProps) {
  const label = current === undefined ? 'All records' : current.name;

  return (
    <div className={cx('flex items-center gap-2', className)}>
      <DropdownMenu
        align="start"
        trigger={
          /* THE VIEW CHIP IS A CONTENT CONTROL (wave 3, L-B2). It was the default glass
             capsule, which is the toolbar's material on the page's ground; the kit's
             content-area push button is what a control standing on the content is made of.
             `size="sm"` for the reason every other capsule in this row takes it: the row
             is desktop chrome above a 54px table row.

             AND THE CHEVRON SITS AFTER THE LABEL (wave 3 round 2, brief item 3), which is
             where the kit's own pull-down puts it: in
             `Pulldown Buttons/Light/Content Area/3 Rg/1 - Idle` - a 120x24 button on the
             same #000000 at 8 percent fill as the Bordered master this variant is built on -
             the chevron glyph sits at x 96 of 120 and the label at x 12, so the mark that
             says "a menu opens here" is at the trailing edge and the reading starts with the
             name. It is `children` rather than `icon` because `Button` draws `icon` before
             its label by construction, and that order is right for every other caller.
             kit: Apple macOS 27 UI Kit.sketch / Pulldown Buttons/Light|Dark/Content Area/3 Rg/1 - Idle */
          <Button variant="push" size="sm" className="min-w-0" aria-label="Choose a view">
            <span className="truncate">{label}</span>
            <Icon name="chevron-down" />
          </Button>
        }
      >
        <DropdownLabel>Views</DropdownLabel>
        <DropdownItem
          icon={current === undefined ? 'check' : undefined}
          onSelect={() => onSelect(null)}
        >
          All records
        </DropdownItem>
        {views.map((view) => (
          <DropdownItem
            key={view.id}
            icon={view.id === current?.id ? 'check' : undefined}
            onSelect={() => onSelect(view.id)}
          >
            {view.isDefault ? `${view.name} (default)` : view.name}
          </DropdownItem>
        ))}

        <DropdownSeparator />
        <DropdownLabel>This list</DropdownLabel>
        <DropdownItem icon="plus" onSelect={onSaveAs}>
          Save as new view…
        </DropdownItem>
        {current !== undefined && (
          <>
            <DropdownItem icon="pencil" onSelect={onRename}>
              Rename…
            </DropdownItem>
            {!current.isDefault && (
              <DropdownItem icon="check" onSelect={onSetDefault}>
                Open by default
              </DropdownItem>
            )}
            <DropdownItem icon="trash" onSelect={onDelete}>
              Delete view
            </DropdownItem>
          </>
        )}
      </DropdownMenu>

      {dirty && current !== undefined && (
        <>
          <span className="hidden items-center gap-1 text-xs text-ink-muted md:inline-flex">
            <Icon name="info" size="0.9rem" />
            Unsaved changes
          </span>
          <Button variant="push" size="sm" loading={isSaving} onClick={onSave}>
            Save
          </Button>
        </>
      )}
    </div>
  );
}
